import React, {
  useEffect, useState, useMemo,
} from 'react'
import Head from 'next/head'
import { SessionProvider } from 'next-auth/react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Hydrate } from 'react-query/hydration'
import { Router, useRouter } from 'next/router'
import { IntlProvider } from 'react-intl'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ThemeProvider } from '@mui/material/styles'
import { GlobalStyles } from '@mui/system'
import esLocale from 'date-fns/locale/es'
import { wrapper } from 'redux/store'
import { PagePropsContext } from 'hooks/usePageProps'
import dynamic from 'next/dynamic'
import { CacheProvider } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import createEmotionCache from 'createEmotionCache'
import theme from 'themes/Clikauto/theme'
import styles from 'clikautoui/styles'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/autoplay'
import 'globals.css'
import LinearProgress from '@mui/material/LinearProgress'
import { ModalProvider } from 'components/shared/Modal/ModalProvider'
import { Modal } from 'components/shared/Modal'
import EventEmitter from 'services/EventEmitter'
import { getPhonesByChannel } from 'utils/universal/getPhonesByChannel'
import { captureUtms } from 'utils/universal/utmStorage'
import { useSelector } from 'react-redux'
import { isIndexedPageSRP } from 'utils/vehicles'
import useNonInitialEffect from 'hooks/useNonInitialEffect'
import { ActionCableProvider } from 'providers/ActionCableProvider'
import { ToggleCollapseProvider } from 'apps/Search/Filters/context/ToggleCollapseContext'
import { Poppins } from 'next/font/google'

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'], // Personaliza los pesos según necesites
  display: 'swap',
})

const DynamicAnalytics = dynamic(
  () => import('components/seo/Analytics'),
  { ssr: false },
)

const clientSideEmotionCache = createEmotionCache()

const MyApp = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const {
    messages,
    canonicalUrl: canonicalUrlProp,
    phones,
    showCanonical: showCanonicalProp,
  } = pageProps

  const { locale, asPath, query } = useRouter()
  const [queryClient] = useState(() => new QueryClient())
  const [loading, setLoading] = useState(false)
  const [showCanonical, setShowCanonical] = useState(showCanonicalProp)
  const [canonicalUrl, setCanonicalUrl] = useState(canonicalUrlProp)
  const { filters } = useSelector((s) => s.search)

  const finalPhones = useMemo(() => getPhonesByChannel(query, phones), [query, phones])

  const isSubastas = asPath.startsWith('/subastas')

  /**
   * Captura de UTMs para la atribución de leads.
   *
   * Se hace a nivel de aplicación porque los UTMs sólo existen en la URL de
   * aterrizaje: en flujos de varios pasos (por ejemplo el cotizador) hay
   * navegaciones que no arrastran el query string, y para cuando el usuario
   * envía el formulario ya se habían perdido. Guardarlos aquí permite que
   * `useLead` los adjunte a cualquier lead posterior de la misma pestaña.
   *
   * Depende de `asPath` para cubrir también las llegadas por navegación
   * interna, no sólo la carga inicial.
   */
  useEffect(() => {
    captureUtms(asPath.split('?')[1] ?? '')
  }, [asPath])

  useEffect(() => {
    const url = (`https://clikauto.com${asPath === '/' ? '' : asPath}`).split('?')[0]
    if (asPath === '/autos-seminuevos/bonus-true') {
      setCanonicalUrl('https://clikauto.com/autos-seminuevos')
    } else {
      setCanonicalUrl(url)
    }
  }, [asPath])

  useNonInitialEffect(() => {
    setShowCanonical(isIndexedPageSRP(filters) || !`${asPath}`.includes('/auto-seminuevo/'))
  }, [filters, asPath])

  useEffect(() => {
    const start = () => {
      setLoading(true)
    }

    const end = () => {
      setLoading(false)
    }

    Router.events.on('routeChangeStart', start)
    Router.events.on('routeChangeComplete', end)
    Router.events.on('routeChangeError', end)

    return () => {
      Router.events.off('routeChangeStart', start)
      Router.events.off('routeChangeComplete', end)
      Router.events.off('routeChangeError', end)
      EventEmitter.removeAllListeners()
    }
  }, [])

  return (
    <main className={poppins.className}>
      <SessionProvider>
        <Head>
          {showCanonical && (
            <link
              rel="canonical"
              href={canonicalUrl}
            />
          )}
        </Head>
        <CacheProvider value={emotionCache}>
          <ThemeProvider theme={theme}>
            {loading && (
              <LinearProgress
                color="primary"
                sx={{
                  position: 'fixed', top: 0, width: '100%', zIndex: 300,
                }}
              />
            )}
            <QueryClientProvider client={queryClient}>
              <Hydrate state={pageProps.dehydratedState}>
                <PagePropsContext.Provider value={{ ...pageProps, ...finalPhones }}>
                  <IntlProvider
                    locale={locale}
                    messages={messages}
                  >
                    <ToggleCollapseProvider>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        locale={esLocale}
                      >

                        <ModalProvider>
                          <Modal />

                          {isSubastas ? (
                            <ActionCableProvider>
                              <Component />
                            </ActionCableProvider>
                          ) : (
                            <Component />
                          )}

                          {process.env.NODE_ENV === 'production' && (
                            <DynamicAnalytics />
                          )}

                        </ModalProvider>
                      </LocalizationProvider>
                    </ToggleCollapseProvider>
                  </IntlProvider>
                </PagePropsContext.Provider>
              </Hydrate>
            </QueryClientProvider>
            <CssBaseline />
            <GlobalStyles styles={styles} />
          </ThemeProvider>
        </CacheProvider>
      </SessionProvider>
    </main>
  )
}

export default wrapper.withRedux(MyApp)
